import React from 'react';
import Select from 'react-select';
import MaskedInput from 'react-text-mask';

import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';

import InspectorOption from '@/components/SchedulePage/OrderModal/OrderForm/InspectorOption/InspectorOption';
import OrderFormContext from '@/components/SchedulePage/OrderModal/OrderForm/OrderFormContext';
import { TIME_MASK } from '@/helpers/mask';
import time from '@/images/icons/time.svg';

const OrderFormComponent = ({
  initialValues,
  inspectors,
  date,
  closeModal,
  onSubmit,
  warningInspectorMessage,
  handleInspectorSelectChange,
  handlePreSelectInspector,
  handleValidateAccompaniedInspection,
  scheduleDateAlert,
  scheduleDate,
  validationSchema,
  intl,
  handleValidateInspectorAvailability,
  warningAvailabilityMessage,
}) => (
  <div className="w-full" style={{ fontFamily: "'Open Sans', sans-serif" }}>
    <Formik
      onSubmit={onSubmit}
      initialValues={initialValues}
      validationSchema={validationSchema}
      validate={handleValidateInspectorAvailability}
    >
      {({ errors, touched, values }) => (
        <Form className="space-y-3">
          <div className="relative p-0 flex flex-col gap-0.5">
            <label className="block text-[10px] font-bold text-[#393939]" style={{ fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase' }} htmlFor="inspector">{intl.formatMessage({ id: 'INSPECTOR' })}</label>

            <Field name="schedule.who.inspector_id">
              {({ field, form }) => (
                <Select
                  {...field}
                  name={field.name}
                  id="inspector"
                  classNamePrefix="select"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minHeight: '34px',
                      height: '34px',
                      fontSize: '0.875em',
                      fontFamily: "'Open Sans', sans-serif",
                      fontWeight: '600',
                      color: '#919191',
                      border: `1px solid ${errors?.schedule?.who?.inspector_id ? '#dc3545' : '#e3e3e3'}`,
                      borderRadius: '3px',
                      boxShadow: 'none',
                      '&:hover': {
                        borderColor: errors?.schedule?.who?.inspector_id ? '#dc3545' : '#e3e3e3',
                      }
                    }),
                    valueContainer: (base) => ({
                      ...base,
                      padding: '0 12px',
                      height: '100%',
                    }),
                    input: (base) => ({
                      ...base,
                      margin: 0,
                      padding: 0,
                      height: '100% !important',
                      '& input': {
                        height: '100% !important',
                      }
                    }),
                    singleValue: (base) => ({
                      ...base,
                      color: '#919191',
                      fontFamily: "'Open Sans', sans-serif",
                      fontWeight: '600',
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: '#b1b1b1',
                      fontFamily: "'Open Sans', sans-serif",
                      fontWeight: '400',
                    })
                  }}
                  defaultValue={handlePreSelectInspector(field)}
                  placeholder={intl.formatMessage({
                    id: 'SELECT_AN_INSPECTOR',
                  })}
                  value={inspectors?.find(option => option.value === field.value || '')}
                  onChange={(selectedOption, triggeredAction) =>
                    handleInspectorSelectChange(selectedOption, triggeredAction, form, field)
                  }
                  onBlur={field.onBlur}
                  noOptionsMessage={() => intl.formatMessage({ id: 'NO_INSPECTOR_AVAILABLE_MESSAGE' })}
                  options={inspectors}
                  isOptionDisabled={option => option.disabled}
                  isClearable
                  required
                  components={{ Option: InspectorOption }}
                />
              )}
            </Field>

            {errors?.schedule?.who?.inspector_id && (
              <div className="order-form-field-error !mt-1">{errors?.schedule?.who?.inspector_id}</div>
            )}

            {warningInspectorMessage && <div className="mt-[0.5em] text-[0.87em] text-[#ff0000]">{warningInspectorMessage}</div>}
          </div>

          <div className="relative p-0 flex flex-col gap-0.5">
            <label className="block text-[10px] font-bold text-[#393939]" style={{ fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase' }} htmlFor="date">{intl.formatMessage({ id: 'DATE' })}</label>

            <Field className="w-full h-[34px] px-[0.5em] border border-[#e3e3e3] rounded-[3px] bg-[#efefef] cursor-not-allowed" style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.875em', fontWeight: '600', color: '#919191' }} component="input" type="text" name="schedule.when.date" id="date" value={date} disabled />
          </div>

          <div className="flex gap-4">
            <div className="relative p-0 flex-1 flex flex-col gap-0.5">
              <label className="block text-[10px] font-bold text-[#393939]" style={{ fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase' }} htmlFor="start-time">{intl.formatMessage({ id: 'START_TIME' })}</label>

              <Field
                name="schedule.when.start_time"
                onChange={handleValidateAccompaniedInspection(values?.schedule?.when)}
              >
                {({ field }) => (
                  <MaskedInput {...field} className={`w-full h-[34px] px-[0.5em] border-[1px] border-solid rounded-[3px] outline-none transition-colors ${errors?.schedule?.when?.start_time && touched?.schedule?.when?.start_time ? 'border-[#dc3545]' : 'border-[#e3e3e3]'} focus:border-[#4d8fca]`} style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.875em', fontWeight: '600', color: '#919191' }} type="text" id="start-time" mask={TIME_MASK} placeholder="hh:mm" />
                )}
              </Field>

              {errors?.schedule?.when?.start_time && touched?.schedule?.when?.start_time && (
                <div className="order-form-field-error !mt-1">{errors?.schedule?.when?.start_time}</div>
              )}

              {warningAvailabilityMessage && (
                <div className="flex justify-start items-center mt-[0.5em]">
                  <p className="m-0 text-[0.87em] text-[#ff0000]">{warningAvailabilityMessage}</p>
                </div>
              )}
            </div>

            <div className="relative p-0 flex-1 flex flex-col gap-0.5">
              <label className="block text-[10px] font-bold text-[#393939]" style={{ fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase' }} htmlFor="end-time">{intl.formatMessage({ id: 'END_TIME' })}</label>

              <Field name="schedule.when.end_time">
                {({ field }) => (
                  <MaskedInput
                    {...field}
                    className={`w-full h-[34px] px-[0.5em] border-[1px] border-solid rounded-[3px] outline-none transition-colors ${values?.schedule?.flextime === true ? 'bg-[#efefef] cursor-not-allowed' : ''} ${errors?.schedule?.when?.end_time && touched?.schedule?.when?.end_time ? 'border-[#dc3545]' : 'border-[#e3e3e3]'} focus:border-[#4d8fca]`}
                    style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.875em', fontWeight: '600', color: values?.schedule?.flextime === true ? '#b1b1b1' : '#919191' }}
                    type="text"
                    id="end-time"
                    mask={TIME_MASK}
                    placeholder="hh:mm"
                    disabled={values?.schedule?.flextime === true}
                  />
                )}
              </Field>

              {errors?.schedule?.when?.end_time && touched?.schedule?.when?.end_time && (
                <div className="order-form-field-error !mt-1">{errors?.schedule?.when?.end_time}</div>
              )}
            </div>
          </div>

          <div className="flex justify-between max-[1300px]:flex-col-reverse gap-4 mb-[26px]">
            <div className="flex items-center gap-2">
              <Field className="w-4 h-4 rounded border-gray-300 text-[#5292cd] focus:ring-[#5292cd]" type="checkbox" name="schedule.flextime" id="flextime" />

              <label className="text-[10px] font-bold uppercase text-[#393939] cursor-pointer" style={{ fontFamily: "'Open Sans', sans-serif" }} htmlFor="flextime">{intl.formatMessage({ id: 'FLEXTIME' })}</label>
            </div>

            {scheduleDateAlert && (
              <div className="mt-2">
                <span className="text-white bg-[#3288b9] text-[10px] rounded-[15px] p-[2px_8px] min-w-[100px] flex items-center gap-1.5 text-center">
                  <img className="w-3 h-3" src={time} alt="time" />
                  {intl.formatMessage({ id: 'scheduleDateAlert' })} - {scheduleDate}
                </span>
              </div>
            )}
          </div>

          <div className="relative p-0 flex flex-col gap-0.5">
            <label className="block text-[10px] font-bold text-[#393939]" style={{ fontFamily: "'Open Sans', sans-serif", textTransform: 'uppercase' }} htmlFor="observation">{intl.formatMessage({ id: 'NOTES_TO_THE_INSPECTOR' })}</label>

            <Field className={`w-full min-h-[100px] px-[0.5em] py-[0.8em] border-[1px] border-solid rounded-[4px] outline-none transition-colors resize-none ${errors?.schedule?.observation ? 'border-[#dc3545]' : 'border-[#e3e3e3]'} focus:border-[#4d8fca]`} style={{ fontFamily: "'Open Sans', sans-serif", fontSize: '0.875em', fontWeight: '600', color: '#919191', lineHeight: '1.3' }} component="textarea" name="schedule.observation" id="observation" />
          </div>

          <div className="flex items-center justify-end gap-3 pt-3">
            <button type="button" className="h-auto w-full max-w-[120px] min-w-0 p-[6px_8px] text-[0.875rem] font-semibold rounded-[3px] bg-[#c1c1c1] text-white hover:opacity-80 transition-opacity" onClick={closeModal}>
              {intl.formatMessage({ id: 'CANCEL' })}
            </button>

            <button type="submit" className="h-auto w-full max-w-[120px] min-w-0 p-[6px_8px] text-[0.875rem] font-semibold rounded-[3px] bg-[#859905] text-white hover:opacity-80 transition-opacity">
              {intl.formatMessage({ id: 'TO_SCHEDULE' })}
            </button>
          </div>

          <OrderFormContext />
        </Form>
      )}
    </Formik>
  </div>
);

OrderFormComponent.propTypes = {
  initialValues: PropTypes.object.isRequired,
  inspectors: PropTypes.array,
  date: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  validationSchema: PropTypes.object.isRequired,
  warningInspectorMessage: PropTypes.string.isRequired,
  handleInspectorSelectChange: PropTypes.func.isRequired,
  handlePreSelectInspector: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  handleValidateInspectorAvailability: PropTypes.func.isRequired,
  warningAvailabilityMessage: PropTypes.string.isRequired,
  handleValidateAccompaniedInspection: PropTypes.func.isRequired,
  scheduleDateAlert: PropTypes.bool,
  scheduleDate: PropTypes.string,
};

OrderFormComponent.defaultProps = {
  inspectors: [],
};

export default OrderFormComponent;
